/**
 * The whole medical file, as the hospital's staff read it.
 *
 * One component for the doctor before the consultation, the nurse at the bed,
 * the clinics manager printing a file and the secretary keeping it current.
 * Everything is read from GET /api/patients/:id/file, which assembles it from
 * the requests themselves — so a lab result appears here the moment the lab
 * completes it, with nothing to copy and nothing to go stale.
 *
 * Money is shown only when the server sent it; this component never decides
 * who may see a price.
 */

import { forwardRef, useEffect, useRef, useState } from 'react';
import { Badge, Button, EmptyState, ErrorState, Field, Icon, Modal, Select, Spinner, Tabs, Textarea } from '../../components/common';
import { patientsApi } from '../../api/endpoints';
import { usePermissions } from '../../context/PermissionContext';
import { useNotifications } from '../../context/NotificationContext';
import { PERMISSIONS } from '../../domain/rbac';
import { RequestCard, RequestResult } from './RequestCard';
import { ASSIGNMENT_META, DEPARTMENT_META, ageFrom, formatDate, formatDateTime, printArea } from './labels';

export const NOTE_KIND_META = Object.freeze({
  initial_diagnosis: { label: 'تشخيص مبدئي', className: 'border-sky-500/30 bg-sky-500/10 text-sky-200' },
  final_diagnosis: { label: 'تشخيص نهائي', className: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-100' },
  complaint: { label: 'شكوى', className: 'border-amber-500/30 bg-amber-500/10 text-amber-200' },
  note: { label: 'ملاحظة', className: 'border-white/10 bg-white/5 text-slate-300' },
  ai_summary: { label: 'ملخص الذكاء الاصطناعي', className: 'border-purple-500/30 bg-purple-500/10 text-purple-200' },
});

const GENDER = { male: 'ذكر', female: 'أنثى' };

const VITAL_LABELS = [
  ['bp', 'الضغط', ''],
  ['pulse', 'النبض', '/د'],
  ['temperature', 'الحرارة', '°'],
  ['spo2', 'الأكسجين', '%'],
  ['respiratoryRate', 'التنفس', '/د'],
  ['glucose', 'السكر', ''],
];

export function vitalsText(payload = {}) {
  return VITAL_LABELS.filter(([key]) => payload[key] !== null && payload[key] !== undefined)
    .map(([key, label, unit]) => `${label} ${payload[key]}${unit}`)
    .join(' · ');
}

function Section({ title, icon, children }) {
  return (
    <section className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
      <h4 className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
        <Icon name={icon} size={16} className="text-cyan-400" /> {title}
      </h4>
      {children}
    </section>
  );
}

/** The white sheet the file prints as. */
export const MedicalFileSheet = forwardRef(function MedicalFileSheet({ file, institutionName }, ref) {
  const p = file.patient;
  const age = ageFrom(p.dateOfBirth);
  const results = file.requests.filter((r) => r.status === 'completed' && r.result);
  return (
    <div ref={ref} className="print-sheet" dir="rtl">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-300 pb-2">
        <div>
          <h2 className="text-base font-bold">{institutionName || 'المنشأة الطبية'}</h2>
          <h1 className="text-xl font-bold">الملف الطبي — {p.fullName}</h1>
        </div>
        <p className="muted text-xs">طُبع في {formatDateTime(new Date())}</p>
      </div>

      <table className="mt-3">
        <tbody>
          <tr>
            <th style={{ width: '22%' }}>السن / النوع</th>
            <td>
              {age !== null ? `${age} سنة` : '—'} {p.gender ? `· ${GENDER[p.gender] || p.gender}` : ''}
            </td>
            <th style={{ width: '22%' }}>نوع المريض</th>
            <td>{p.patientType === 'insured' ? `تأمين — ${p.insuranceProvider || ''}` : 'نقدي'}</td>
          </tr>
          <tr>
            <th>الرقم القومي</th>
            <td dir="ltr">{p.nationalId || '—'}</td>
            <th>الهاتف</th>
            <td dir="ltr">{[p.phonePrimary, p.phoneSecondary].filter(Boolean).join(' / ') || '—'}</td>
          </tr>
          <tr>
            <th>الأمراض المزمنة</th>
            <td colSpan={3}>{(p.chronicDiseases || []).join('، ') || '—'}</td>
          </tr>
          <tr>
            <th>الشكوى</th>
            <td colSpan={3}>{p.complaints || '—'}</td>
          </tr>
          <tr>
            <th>التاريخ المرضي</th>
            <td colSpan={3}>{p.medicalHistory || '—'}</td>
          </tr>
        </tbody>
      </table>

      {(p.currentMedications || []).length > 0 && (
        <>
          <h3 className="mt-3 font-bold">الأدوية الحالية</h3>
          <table>
            <thead>
              <tr>
                <th>الدواء</th>
                <th>التركيز</th>
                <th>الجرعة</th>
                <th>المدة</th>
              </tr>
            </thead>
            <tbody>
              {p.currentMedications.map((m, i) => (
                <tr key={i}>
                  <td>{m.name}</td>
                  <td>{m.concentration || '—'}</td>
                  <td>{m.frequency || '—'}</td>
                  <td>{m.duration || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {file.notes.length > 0 && (
        <>
          <h3 className="mt-3 font-bold">التشخيصات والملاحظات</h3>
          <table>
            <tbody>
              {file.notes
                .filter((n) => n.kind !== 'ai_summary')
                .map((n) => (
                  <tr key={n.id}>
                    <th style={{ width: '22%' }}>
                      {NOTE_KIND_META[n.kind]?.label || n.kind}
                      <div className="muted text-[10px]">{formatDate(n.createdAt)}</div>
                    </th>
                    <td className="whitespace-pre-line">{n.text}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </>
      )}

      {results.length > 0 && (
        <>
          <h3 className="mt-3 font-bold">النتائج</h3>
          <table>
            <tbody>
              {results.map((r) => (
                <tr key={r.id}>
                  <th style={{ width: '22%' }}>
                    {DEPARTMENT_META[r.department]?.label} — {r.title}
                    <div className="muted text-[10px]">{formatDate(r.completedAt)}</div>
                  </th>
                  <td>
                    {r.result.summary || r.result.impression || ''}
                    {Array.isArray(r.result.values) &&
                      r.result.values.map((v, i) => (
                        <div key={i}>
                          {v.test}: {v.value} {v.unit} {v.range ? `(${v.range})` : ''}
                        </div>
                      ))}
                    {r.result.report && <div className="whitespace-pre-line">{r.result.report}</div>}
                    {Array.isArray(r.result.dispensed) &&
                      r.result.dispensed.map((d, i) => (
                        <div key={i}>
                          {d.name} {d.quantity ? `— ${d.quantity}` : ''}
                        </div>
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {file.prescriptions.length > 0 && (
        <>
          <h3 className="mt-3 font-bold">الروشتات</h3>
          <table>
            <tbody>
              {file.prescriptions.map((rx) => (
                <tr key={rx.id}>
                  <th style={{ width: '22%' }}>
                    {rx.doctorName || ''}
                    <div className="muted text-[10px]">{formatDate(rx.date || rx.createdAt)}</div>
                  </th>
                  <td>
                    {rx.diagnosis && <div>التشخيص: {rx.diagnosis}</div>}
                    {(rx.medicines || []).map((m, i) => (
                      <div key={i}>
                        {m.name} {m.dosage || ''} {m.frequency || ''} {m.duration || ''}
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
});

export function MedicalFileView({ patientId, bookingId = null, onEdit }) {
  const { can, institutionName } = usePermissions();
  const { success, error: toastError } = useNotifications();
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('summary');
  const [printing, setPrinting] = useState(false);
  const [noteKind, setNoteKind] = useState('note');
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const sheet = useRef(null);

  const canAppend = can(PERMISSIONS.PATIENT_RECORD_APPEND);
  const canDiagnose = can(PERMISSIONS.PRESCRIPTION_WRITE);

  const load = async () => {
    setError(null);
    try {
      setFile(await patientsApi.file(patientId));
    } catch (err) {
      setError(err);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await patientsApi.addNote(patientId, { kind: noteKind, text: noteText.trim(), bookingId });
      setNoteText('');
      success('تمت الإضافة إلى الملف');
      load();
    } catch (err) {
      toastError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openAttachment = async (row) => {
    try {
      const full = await patientsApi.attachment(row.id);
      const win = window.open();
      if (!win) return;
      if (String(full.mimeType).startsWith('image/')) win.document.body.innerHTML = `<img src="${full.dataUrl}" style="max-width:100%">`;
      else win.location.href = full.dataUrl;
    } catch (err) {
      toastError(err.message);
    }
  };

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!file) return <Spinner label="جارٍ تحميل الملف الطبي" variant="inline" />;

  const p = file.patient;
  const age = ageFrom(p.dateOfBirth);
  const results = file.requests.filter((r) => r.status === 'completed');
  const open = file.requests.filter((r) => !['completed', 'cancelled', 'rejected'].includes(r.status));

  const tabs = [
    { id: 'summary', label: 'الملخص', icon: 'file-heart' },
    { id: 'notes', label: 'التشخيصات', icon: 'notebook-pen', count: file.notes.length },
    { id: 'results', label: 'النتائج', icon: 'file-check-2', count: results.length },
    { id: 'requests', label: 'طلبات جارية', icon: 'list-todo', count: open.length },
    { id: 'visits', label: 'الزيارات والروشتات', icon: 'calendar-days', count: file.visits.length },
    { id: 'nursing', label: 'التمريض والإقامة', icon: 'bed-double', count: file.nursing.length + file.beds.length },
    { id: 'attachments', label: 'المرفقات', icon: 'paperclip', count: file.attachments.length },
  ];

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className={`tile3d__face ${p.patientType === 'insured' ? 'tone-purple' : 'tone-emerald'} shrink-0`} aria-hidden="true">
          <Icon name={p.patientType === 'insured' ? 'shield-plus' : 'user'} size={24} className="tile3d__icon" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="break-words text-lg font-bold text-white">{p.fullName}</h3>
          <p className="text-xs text-slate-400">
            {age !== null ? `${age} سنة` : 'السن غير مسجل'}
            {p.gender ? ` · ${GENDER[p.gender]}` : ''}
            {p.patientType === 'insured' ? ` · تأمين ${p.insuranceProvider || ''}` : ' · نقدي'}
            {p.insuranceExpiry ? ` · ينتهي ${formatDate(p.insuranceExpiry)}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {onEdit && (
            <Button variant="subtle" icon="pencil" onClick={onEdit}>
              تعديل البيانات
            </Button>
          )}
          <Button variant="ghost" icon="printer" onClick={() => setPrinting(true)}>
            طباعة الملف
          </Button>
        </div>
      </div>

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {tab === 'summary' && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Section title="الأمراض المزمنة" icon="heart-pulse">
            {(p.chronicDiseases || []).length ? (
              <div className="flex flex-wrap gap-1.5">
                {p.chronicDiseases.map((d) => (
                  <Badge key={d} className="border-rose-500/30 bg-rose-500/10 text-rose-200">
                    {d}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">لا يوجد</p>
            )}
          </Section>
          <Section title="الأدوية الحالية" icon="pill">
            {(p.currentMedications || []).length ? (
              <ul className="space-y-1 text-sm text-slate-200">
                {p.currentMedications.map((m, i) => (
                  <li key={i}>
                    <b>{m.name}</b> {m.concentration && <span className="text-slate-400">{m.concentration}</span>}
                    {m.frequency && <span className="text-slate-400"> · {m.frequency}</span>}
                    {m.duration && <span className="text-slate-400"> · {m.duration}</span>}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500">لا يوجد</p>
            )}
          </Section>
          <Section title="الشكوى" icon="message-square-text">
            <p className="whitespace-pre-line text-sm text-slate-200">{p.complaints || '—'}</p>
          </Section>
          <Section title="التاريخ المرضي" icon="history">
            <p className="whitespace-pre-line text-sm text-slate-200">{p.medicalHistory || '—'}</p>
          </Section>
          {file.notes.find((n) => n.kind === 'ai_summary') && (
            <Section title="آخر ملخص بالذكاء الاصطناعي" icon="sparkles">
              <p className="whitespace-pre-line text-sm text-purple-100">{file.notes.find((n) => n.kind === 'ai_summary').text}</p>
              <p className="mt-2 text-[11px] text-slate-500">ملخص مساعد للطبيب وليس تشخيصاً.</p>
            </Section>
          )}
        </div>
      )}

      {tab === 'notes' && (
        <div className="space-y-3">
          {canAppend && (
            <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/[0.05] p-3">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-[12rem_1fr_auto]">
                <Field label="النوع">
                  <Select value={noteKind} onChange={(e) => setNoteKind(e.target.value)}>
                    <option value="note">ملاحظة</option>
                    <option value="complaint">شكوى</option>
                    {canDiagnose && <option value="initial_diagnosis">تشخيص مبدئي</option>}
                    {canDiagnose && <option value="final_diagnosis">تشخيص نهائي</option>}
                  </Select>
                </Field>
                <Field label="النص">
                  <Textarea rows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)} />
                </Field>
                <div className="flex items-end">
                  <Button icon="plus" loading={saving} onClick={addNote}>
                    إضافة
                  </Button>
                </div>
              </div>
            </div>
          )}
          {file.notes.length === 0 ? (
            <EmptyState icon="notebook-pen" title="لا توجد تشخيصات أو ملاحظات بعد" />
          ) : (
            file.notes.map((n) => (
              <div key={n.id} className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
                <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                  <Badge className={NOTE_KIND_META[n.kind]?.className}>{NOTE_KIND_META[n.kind]?.label || n.kind}</Badge>
                  {n.createdByName && <span>{n.createdByName}</span>}
                  <span>{formatDateTime(n.createdAt)}</span>
                </div>
                <p className="whitespace-pre-line text-sm text-slate-100">{n.text}</p>
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'results' && (
        <div className="space-y-3">
          {results.length === 0 ? (
            <EmptyState icon="file-check-2" title="لا توجد نتائج بعد" message="تظهر نتائج المعمل والأشعة والصيدلية هنا فور اعتمادها." />
          ) : (
            results.map((r) => (
              <div key={r.id} className="rounded-xl border border-emerald-500/15 bg-emerald-500/[0.04] p-3">
                <p className="mb-2 flex flex-wrap items-center gap-2 text-sm font-bold text-white">
                  <Icon name={DEPARTMENT_META[r.department]?.icon || 'file'} size={15} className="text-emerald-300" />
                  {DEPARTMENT_META[r.department]?.label} — {r.title}
                  <span className="text-xs font-normal text-slate-400">{formatDateTime(r.completedAt)}</span>
                </p>
                <RequestResult request={r} />
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'requests' && (
        <div className="space-y-3">
          {open.length === 0 ? (
            <EmptyState icon="list-todo" title="لا توجد طلبات جارية" />
          ) : (
            open.map((r) => <RequestCard key={r.id} request={r} showPatient={false} showMoney={Boolean(r.quote)} />)
          )}
        </div>
      )}

      {tab === 'visits' && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Section title="الزيارات" icon="calendar-days">
            {file.visits.length === 0 ? (
              <p className="text-sm text-slate-500">لا توجد زيارات مسجلة</p>
            ) : (
              <ul className="space-y-1 text-sm text-slate-200">
                {file.visits.map((v) => (
                  <li key={v.id} className="flex flex-wrap justify-between gap-2">
                    <span>
                      {v.date} {v.time} · {v.doctorName || ''} {v.clinicName ? `· ${v.clinicName}` : ''}
                    </span>
                    <span className="text-xs text-slate-400">{v.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
          <Section title="الروشتات" icon="scroll-text">
            {file.prescriptions.length === 0 ? (
              <p className="text-sm text-slate-500">لا توجد روشتات</p>
            ) : (
              <ul className="space-y-2 text-sm text-slate-200">
                {file.prescriptions.map((rx) => (
                  <li key={rx.id} className="rounded-lg bg-white/[0.03] p-2">
                    <p className="text-xs text-slate-400">
                      {formatDate(rx.date || rx.createdAt)} · {rx.doctorName || ''}
                    </p>
                    {rx.diagnosis && <p className="text-slate-100">{rx.diagnosis}</p>}
                    {(rx.medicines || []).map((m, i) => (
                      <p key={i} className="text-xs">
                        • {m.name} {m.dosage || ''} {m.frequency || ''} {m.duration || ''}
                      </p>
                    ))}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      )}

      {tab === 'nursing' && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Section title="الإقامة بالأسرّة" icon="bed-double">
            {file.beds.length === 0 ? (
              <p className="text-sm text-slate-500">لم يُحجز سرير</p>
            ) : (
              <ul className="space-y-1 text-sm text-slate-200">
                {file.beds.map((b) => (
                  <li key={b.id} className="flex flex-wrap justify-between gap-2">
                    <span>
                      سرير #{b.bedId} · {formatDateTime(b.startAt)}
                      {b.endedAt ? ` ← ${formatDateTime(b.endedAt)}` : ''}
                    </span>
                    <span className={`rounded px-1.5 text-xs ${ASSIGNMENT_META[b.status]?.className || ''}`}>{ASSIGNMENT_META[b.status]?.label}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
          <Section title="سجل التمريض" icon="activity">
            {file.nursing.length === 0 ? (
              <p className="text-sm text-slate-500">لا توجد تسجيلات</p>
            ) : (
              <ul className="space-y-1.5 text-sm text-slate-200">
                {file.nursing.map((n) => (
                  <li key={n.id} className="rounded-lg bg-white/[0.03] p-2">
                    <p className="text-[11px] text-slate-400">
                      {formatDateTime(n.recordedAt)} {n.recordedByName ? `· ${n.recordedByName}` : ''}
                    </p>
                    {n.kind === 'vitals' && <p>{vitalsText(n.payload)}</p>}
                    {n.kind === 'medication' && (
                      <p>
                        💊 {n.payload?.name} {n.payload?.dose || ''} {n.payload?.route || ''}
                      </p>
                    )}
                    {n.text && <p className="whitespace-pre-line">{n.text}</p>}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      )}

      {tab === 'attachments' && (
        <div>
          {file.attachments.length === 0 ? (
            <EmptyState icon="paperclip" title="لا توجد مرفقات" />
          ) : (
            <ul className="divide-y divide-white/5 rounded-xl border border-white/5">
              {file.attachments.map((a) => (
                <li key={a.id} className="flex items-center gap-3 p-3">
                  <Icon name="file-text" size={16} className="text-cyan-400" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-white">{a.title || a.fileName}</span>
                    <span className="block text-[11px] text-slate-500">{a.category}</span>
                  </span>
                  <Button variant="subtle" icon="eye" onClick={() => openAttachment(a)}>
                    عرض
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Modal
        open={printing}
        onClose={() => setPrinting(false)}
        title="طباعة الملف الطبي"
        icon="printer"
        size="xl"
        footer={
          <div className="flex justify-end">
            <Button icon="printer" onClick={() => printArea(sheet.current)}>
              طباعة / حفظ PDF
            </Button>
          </div>
        }
      >
        <MedicalFileSheet ref={sheet} file={file} institutionName={institutionName} />
      </Modal>
    </div>
  );
}

export default MedicalFileView;
